/**
 * <academy-countdown> — countdown chip for the Academy Color Slider content
 * column. Ticks once a second, renders days / hours / minutes / seconds and
 * switches between three "traffic-light" states based on the absolute time
 * still left on the clock (thresholds are set per section, in seconds):
 *
 *   high   remaining  >  data-mid-seconds  → .acs-countdown--high  (info-soft   #E8F0FE)
 *   mid    data-low-seconds < remaining <= data-mid-seconds → .acs-countdown--mid (warning-soft #FEE5C7)
 *   low    remaining <= data-low-seconds   → .acs-countdown--low   (error-light  #FCE8E4)
 *
 * The background colors live in CSS (.acs-countdown--{high,mid,low}); this
 * script only toggles the modifier class and swaps the headline text from
 * data-text-{high,mid,low}.
 *
 * Attributes:
 *   data-end          end timestamp, "YYYY-MM-DD HH:MM" (shop timezone) or ISO
 *   data-mid-seconds  remaining seconds at/below which the "mid" state starts
 *   data-low-seconds  remaining seconds at/below which the "low" state starts
 *   data-text-high    headline text for the "high" state
 *   data-text-mid     headline text for the "mid" state
 *   data-text-low     headline text for the "low" state
 *   data-design-mode  "true" inside the Shopify theme editor — the block then
 *                     never hides on expiry so it stays selectable/stylable
 *
 * Re-inits per instance in connectedCallback (the theme editor re-renders the
 * whole section on every settings change) and clears its timers on disconnect.
 */
if (!customElements.get('academy-countdown')) {
  class AcademyCountdown extends HTMLElement {
    connectedCallback() {
      if (this._inited) return;
      this._inited = true;

      this._els = {
        days: this.querySelector('[data-cd="days"]'),
        hours: this.querySelector('[data-cd="hours"]'),
        minutes: this.querySelector('[data-cd="minutes"]'),
        seconds: this.querySelector('[data-cd="seconds"]'),
        label: this.querySelector('[data-cd-label]'),
      };

      this._end = this._parseDate(this.dataset.end);
      var midS = parseFloat(this.dataset.midSeconds);
      var lowS = parseFloat(this.dataset.lowSeconds);
      this._midMs = isFinite(midS) && midS > 0 ? midS * 1000 : 72 * 3600000;
      this._lowMs = isFinite(lowS) && lowS > 0 ? lowS * 1000 : 24 * 3600000;
      this._designMode = this.dataset.designMode === 'true';
      this._state = null;
      this._reduce =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this._end) return;

      this._tick = this._tick.bind(this);
      this._tick();

      // Align the first interval to the next whole second so the digits flip
      // on the second, then settle into a plain 1s interval.
      this._timeout = setTimeout(
        function () {
          this._tick();
          this._interval = setInterval(this._tick, 1000);
        }.bind(this),
        1000 - (Date.now() % 1000)
      );
    }

    disconnectedCallback() {
      this._inited = false;
      clearTimeout(this._timeout);
      clearInterval(this._interval);
    }

    _parseDate(raw) {
      if (!raw) return null;
      var s = String(raw).trim();
      if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(s)) {
        // "YYYY-MM-DD HH:MM[:SS]" → ISO-ish, parsed as visitor local time
        s = s.replace(' ', 'T');
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        s = s + 'T00:00';
      }
      var d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    }

    _pad(n) {
      return (n < 10 ? '0' : '') + n;
    }

    _tick() {
      var remaining = this._end.getTime() - Date.now();

      if (remaining <= 0) {
        this._set(0, 0, 0, 0);
        this._applyState('low');
        if (!this._designMode) {
          this.hidden = true;
          clearTimeout(this._timeout);
          clearInterval(this._interval);
        }
        return;
      }

      var totalSec = Math.floor(remaining / 1000);
      this._set(
        Math.floor(totalSec / 86400),
        Math.floor((totalSec % 86400) / 3600),
        Math.floor((totalSec % 3600) / 60),
        totalSec % 60
      );

      this._applyState(
        remaining > this._midMs ? 'high' : remaining > this._lowMs ? 'mid' : 'low'
      );
    }

    _set(d, h, m, s) {
      if (this._els.days) this._els.days.textContent = this._pad(d);
      if (this._els.hours) this._els.hours.textContent = this._pad(h);
      if (this._els.minutes) this._els.minutes.textContent = this._pad(m);
      if (this._els.seconds) this._els.seconds.textContent = this._pad(s);
    }

    _applyState(state) {
      if (state === this._state) return;
      this._state = state;

      this.classList.remove(
        'acs-countdown--high',
        'acs-countdown--mid',
        'acs-countdown--low'
      );
      this.classList.add('acs-countdown--' + state);

      var text = this.dataset[
        'text' + state.charAt(0).toUpperCase() + state.slice(1)
      ];
      if (text) {
        if (this._els.label) this._els.label.textContent = text;
        this.setAttribute('aria-label', text);
      }

      if (!this._reduce) {
        this.classList.remove('is-bump');
        void this.offsetWidth;
        this.classList.add('is-bump');
      }
    }
  }

  customElements.define('academy-countdown', AcademyCountdown);
}
